import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import { FaSpotify, FaApple, FaYoutube } from 'react-icons/fa';
import SafeIcon from '../SafeIcon';
import ColorPickerMock from './ColorPickerMock';
import supabase from '../../supabase/supabase';
import { compressImage } from '../../utils/imageCompression';

const { FiX, FiCheck, FiMusic, FiPlus, FiTrash2, FiEdit2, FiUploadCloud } = FiIcons;

const ArtistWidgetPopup = ({ onClose, onUpdate, currentStyles = {} }) => {
  const [settings, setSettings] = useState({
    tracks: currentStyles.tracks || [],
    showArtistName: currentStyles.showArtistName !== false,
    artistName: currentStyles.artistName || 'Artist Name',
    cardBgColor: currentStyles.cardBgColor || '#ffffff',
    textColor: currentStyles.textColor || '#111827',
    accentColor: currentStyles.accentColor || '#5494d4',
    borderRadius: currentStyles.borderRadius || 16,
  });

  const [editingTrack, setEditingTrack] = useState(null);
  const [uploadingCover, setUploadingCover] = useState(false);
  const [uploadingAudio, setUploadingAudio] = useState(false);

  useEffect(() => {
    setSettings({
      tracks: currentStyles.tracks || [],
      showArtistName: currentStyles.showArtistName !== false,
      artistName: currentStyles.artistName || 'Artist Name',
      cardBgColor: currentStyles.cardBgColor || '#ffffff',
      textColor: currentStyles.textColor || '#111827',
      accentColor: currentStyles.accentColor || '#5494d4',
      borderRadius: currentStyles.borderRadius || 16,
    });
  }, [currentStyles]);

  const handleAddTrack = () => {
    const newTrack = {
      id: Date.now(),
      title: 'New Track',
      artist: '',
      description: '',
      coverUrl: '',
      audioUrl: '',
      links: {
        spotify: '',
        apple: '',
        youtube: ''
      }
    };
    setSettings(s => ({ ...s, tracks: [...s.tracks, newTrack] }));
    setEditingTrack(newTrack);
  };

  const handleUpdateTrack = (trackId, updates) => {
    setSettings(s => ({
      ...s,
      tracks: s.tracks.map(t => t.id === trackId ? { ...t, ...updates } : t)
    }));
    if (editingTrack?.id === trackId) {
      setEditingTrack(prev => ({ ...prev, ...updates }));
    }
  };

  const handleDeleteTrack = (trackId) => {
    setSettings(s => ({
      ...s,
      tracks: s.tracks.filter(t => t.id !== trackId)
    }));
    if (editingTrack?.id === trackId) {
      setEditingTrack(null);
    }
  };

  const handleCoverUpload = async (trackId, file) => {
    if (!file) return;
    setUploadingCover(true);

    try {
      const compressed = await compressImage(file, {
        maxWidth: 800,
        maxHeight: 800,
        quality: 0.85
      });

      const fileExt = file.name.split('.').pop();
      const fileName = `music-covers/${trackId}-${Date.now()}.${fileExt}`;

      const { data: uploadData, error: uploadError } = await supabase.storage
        .from('media')
        .upload(`images/${fileName}`, compressed, {
          upsert: true,
          contentType: compressed.type
        });

      if (uploadError) throw uploadError;

      const { data: urlData } = supabase.storage
        .from('media')
        .getPublicUrl(`images/${fileName}`);

      if (!urlData?.publicUrl) throw new Error('Failed to get public URL');

      handleUpdateTrack(trackId, { coverUrl: urlData.publicUrl });
    } catch (err) {
      console.error('Error uploading cover:', err);
      alert(`Failed to upload cover: ${err.message}`);
    } finally {
      setUploadingCover(false);
    }
  };

  const handleAudioUpload = async (trackId, file) => {
    if (!file) return;
    setUploadingAudio(true);
    
    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `music-audio/${trackId}-${Date.now()}.${fileExt}`;

      const { data: uploadData, error: uploadError } = await supabase.storage
        .from('media')
        .upload(`audio/${fileName}`, file, {
          upsert: true,
          contentType: file.type
        });

      if (uploadError) throw uploadError;

      const { data: urlData } = supabase.storage
        .from('media')
        .getPublicUrl(`audio/${fileName}`);

      if (!urlData?.publicUrl) throw new Error('Failed to get public URL');

      handleUpdateTrack(trackId, { audioUrl: urlData.publicUrl });
    } catch (err) {
      console.error('Error uploading audio:', err);
      alert(`Failed to upload audio: ${err.message}`);
    } finally {
      setUploadingAudio(false);
    }
  };

  const handleApply = () => {
    onUpdate(settings);
    onClose();
  };

  const platforms = [
    { id: 'spotify', name: 'Spotify', icon: FaSpotify, color: '#1DB954', placeholder: 'https://open.spotify.com/track/...' },
    { id: 'apple', name: 'Apple Music', icon: FaApple, color: '#FA2D48', placeholder: 'https://music.apple.com/...' },
    { id: 'youtube', name: 'YouTube', icon: FaYoutube, color: '#FF0000', placeholder: 'https://youtube.com/watch?v=...' }
  ];

  return (
    <div className="fixed inset-0 z-[110] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        className="bg-white rounded-3xl shadow-2xl w-full max-w-6xl overflow-hidden max-h-[90vh] flex flex-col"
      >
        {/* Header */}
        <div className="p-5 border-b border-gray-100 flex justify-between items-center bg-gradient-to-r from-purple-50 to-blue-50">
          <div className="flex items-center space-x-3">
            <div className="p-2.5 bg-purple-500 rounded-xl">
              <SafeIcon icon={FiMusic} className="w-5 h-5 text-white" />
            </div>
            <div>
              <h3 className="font-black text-gray-900 text-lg uppercase tracking-tight">Artist Widget Settings</h3>
              <p className="text-xs text-gray-500 font-medium">Manage your music tracks and style</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-white rounded-full transition-colors text-gray-400 hover:text-gray-600">
            <SafeIcon icon={FiX} className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 p-6">
            {/* Left Column - Tracks Management */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h4 className="text-[10px] font-black text-gray-400 uppercase tracking-widest">
                  Music Tracks ({settings.tracks.length})
                </h4>
                <button
                  onClick={handleAddTrack}
                  className="px-3 py-1.5 bg-purple-500 hover:bg-purple-600 text-white rounded-lg text-xs font-bold flex items-center space-x-1 transition-colors"
                >
                  <SafeIcon icon={FiPlus} className="w-3 h-3" />
                  <span>Add Track</span>
                </button>
              </div>

              <div className="space-y-3 max-h-[600px] overflow-y-auto pr-2 custom-scrollbar">
                <AnimatePresence>
                  {settings.tracks.map((track) => (
                    <motion.div
                      key={track.id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, x: -20 }}
                      className={`border rounded-xl p-4 transition-all ${
                        editingTrack?.id === track.id 
                          ? 'border-purple-400 bg-purple-50/50 shadow-lg' 
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      {editingTrack?.id === track.id ? (
                        <div className="space-y-4">
                          {/* Cover Upload */}
                          <div className="flex items-start space-x-4">
                            {track.coverUrl ? (
                              <div className="relative group">
                                <img 
                                  src={track.coverUrl} 
                                  alt={track.title}
                                  className="w-24 h-24 rounded-xl object-cover border-2 border-purple-400"
                                />
                                <label className="absolute inset-0 bg-black/50 rounded-xl flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer">
                                  <input
                                    type="file"
                                    accept="image/*"
                                    className="hidden"
                                    onChange={(e) => {
                                      const file = e.target.files?.[0];
                                      if (file) handleCoverUpload(track.id, file);
                                      e.target.value = '';
                                    }}
                                    disabled={uploadingCover}
                                  />
                                  <SafeIcon icon={FiUploadCloud} className="w-6 h-6 text-white" />
                                </label>
                              </div>
                            ) : (
                              <label className="w-24 h-24 rounded-xl border-2 border-dashed border-purple-300 bg-purple-50 flex items-center justify-center cursor-pointer hover:border-purple-400 transition-colors">
                                <input
                                  type="file"
                                  accept="image/*"
                                  className="hidden"
                                  onChange={(e) => {
                                    const file = e.target.files?.[0];
                                    if (file) handleCoverUpload(track.id, file);
                                    e.target.value = '';
                                  }}
                                  disabled={uploadingCover}
                                />
                                {uploadingCover ? (
                                  <SafeIcon icon={FiMusic} className="w-6 h-6 text-purple-400 animate-pulse" />
                                ) : (
                                  <SafeIcon icon={FiUploadCloud} className="w-6 h-6 text-purple-400" />
                                )}
                              </label>
                            )}
                            <div className="flex-1 space-y-2">
                              <input
                                type="text"
                                value={track.title}
                                onChange={(e) => handleUpdateTrack(track.id, { title: e.target.value })}
                                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm font-bold focus:outline-none focus:border-purple-400"
                                placeholder="Track Title"
                              />
                              <input
                                type="text"
                                value={track.artist || ''}
                                onChange={(e) => handleUpdateTrack(track.id, { artist: e.target.value })}
                                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-xs focus:outline-none focus:border-purple-400"
                                placeholder="Artist Name (optional)"
                              />
                            </div>
                          </div>

                          {/* Description */}
                          <div className="space-y-2">
                            <label className="text-[9px] font-black text-gray-400 uppercase tracking-widest">
                              Track Description
                            </label>
                            <textarea
                              value={track.description || ''}
                              onChange={(e) => handleUpdateTrack(track.id, { description: e.target.value })}
                              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-xs focus:outline-none focus:border-purple-400 resize-none"
                              placeholder="Add details about this track (optional)"
                              rows={2}
                            />
                          </div>

                          {/* Audio Upload */}
                          <div className="space-y-2 border-t border-gray-100 pt-3">
                            <label className="text-[9px] font-black text-gray-400 uppercase tracking-widest">
                              Audio Preview (30s max)
                            </label>
                            {track.audioUrl ? (
                              <div className="flex items-center space-x-3 bg-gray-50/80 p-2.5 rounded-lg border border-gray-200">
                                <div className="p-2 bg-purple-100 rounded-md">
                                  <SafeIcon icon={FiMusic} className="w-4 h-4 text-purple-500" />
                                </div>
                                <span className="flex-1 text-xs font-bold text-gray-700 truncate">Audio track uploaded</span>
                                <button
                                  onClick={() => handleUpdateTrack(track.id, { audioUrl: '' })}
                                  className="p-1.5 bg-red-100 text-red-500 rounded-md hover:bg-red-200 transition-colors"
                                  title="Remove audio preview"
                                >
                                  <SafeIcon icon={FiTrash2} className="w-3.5 h-3.5" />
                                </button>
                              </div>
                            ) : (
                              <label className="flex items-center justify-center space-x-2 w-full py-2.5 border-2 border-dashed border-gray-300 rounded-lg hover:border-purple-400 hover:bg-purple-50 cursor-pointer transition-all">
                                <input
                                  type="file"
                                  accept="audio/*"
                                  className="hidden"
                                  onChange={(e) => {
                                    const file = e.target.files?.[0];
                                    if (file) handleAudioUpload(track.id, file);
                                    e.target.value = '';
                                  }}
                                  disabled={uploadingAudio}
                                />
                                {uploadingAudio ? (
                                  <>
                                    <SafeIcon icon={FiUploadCloud} className="w-4 h-4 text-purple-400 animate-bounce" />
                                    <span className="text-xs font-bold text-purple-400">Uploading Audio...</span>
                                  </>
                                ) : (
                                  <>
                                    <SafeIcon icon={FiUploadCloud} className="w-4 h-4 text-gray-400" />
                                    <span className="text-xs font-bold text-gray-500">Upload MP3/WAV Preview</span>
                                  </>
                                )}
                              </label>
                            )}
                          </div>

                          {/* Platform Links */}
                          <div className="space-y-3 border-t border-gray-100 pt-3">
                            <label className="text-[9px] font-black text-gray-400 uppercase tracking-widest">
                              Streaming Links
                            </label>
                            {platforms.map((platform) => (
                              <div key={platform.id} className="flex items-center space-x-2">
                                <div 
                                  className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0"
                                  style={{ backgroundColor: platform.color }}
                                >
                                  <SafeIcon icon={platform.icon} className="w-4 h-4 text-white" />
                                </div>
                                <input
                                  type="url"
                                  value={track.links?.[platform.id] || ''}
                                  onChange={(e) => handleUpdateTrack(track.id, {
                                    links: { ...track.links, [platform.id]: e.target.value }
                                  })}
                                  className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-xs focus:outline-none focus:border-purple-400"
                                  placeholder={platform.placeholder}
                                />
                              </div>
                            ))}
                          </div>

                          <div className="flex space-x-2 pt-2">
                            <button
                              onClick={() => setEditingTrack(null)}
                              className="flex-1 px-3 py-2.5 bg-green-500 hover:bg-green-600 text-white rounded-lg text-xs font-bold transition-colors"
                            >
                              Done Editing
                            </button>
                            <button
                              onClick={() => handleDeleteTrack(track.id)}
                              className="px-3 py-2.5 bg-red-500 hover:bg-red-600 text-white rounded-lg text-xs font-bold transition-colors"
                            >
                              <SafeIcon icon={FiTrash2} className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="flex items-center space-x-3">
                          {track.coverUrl ? (
                            <img 
                              src={track.coverUrl} 
                              alt={track.title}
                              className="w-16 h-16 rounded-lg object-cover"
                            />
                          ) : (
                            <div className="w-16 h-16 rounded-lg bg-gray-100 flex items-center justify-center">
                              <SafeIcon icon={FiMusic} className="w-6 h-6 text-gray-300" />
                            </div>
                          )}
                          <div className="flex-1 min-w-0">
                            <div className="font-bold text-sm text-gray-900 truncate">{track.title}</div>
                            {track.artist && (
                              <div className="text-xs text-gray-500 truncate">{track.artist}</div>
                            )}
                            {track.description && (
                              <div className="text-xs text-gray-400 truncate mt-0.5">{track.description}</div>
                            )}
                            <div className="flex items-center space-x-1.5 mt-1.5">
                              {track.audioUrl && (
                                <div className="px-1.5 py-0.5 rounded bg-purple-100 border border-purple-200 flex items-center justify-center mr-1">
                                  <span className="text-[9px] font-black tracking-widest uppercase text-purple-600">Audio</span>
                                </div>
                              )}
                              {track.links?.spotify && (
                                <div className="w-4 h-4 rounded-full bg-[#1DB954] flex items-center justify-center">
                                  <SafeIcon icon={FaSpotify} className="w-2.5 h-2.5 text-white" />
                                </div>
                              )}
                              {track.links?.apple && (
                                <div className="w-4 h-4 rounded-full bg-[#FA2D48] flex items-center justify-center">
                                  <SafeIcon icon={FaApple} className="w-2.5 h-2.5 text-white" />
                                </div>
                              )}
                              {track.links?.youtube && (
                                <div className="w-4 h-4 rounded-full bg-[#FF0000] flex items-center justify-center">
                                  <SafeIcon icon={FaYoutube} className="w-2.5 h-2.5 text-white" />
                                </div>
                              )}
                            </div>
                          </div>
                          <button
                            onClick={() => setEditingTrack(track)}
                            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                          >
                            <SafeIcon icon={FiEdit2} className="w-4 h-4 text-gray-400" />
                          </button>
                        </div>
                      )}
                    </motion.div>
                  ))}
                </AnimatePresence>

                {settings.tracks.length === 0 && (
                  <div className="text-center py-12 text-gray-400">
                    <SafeIcon icon={FiMusic} className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p className="text-sm font-medium">No tracks added yet</p>
                    <p className="text-xs mt-1">Click "Add Track" to get started</p>
                  </div>
                )}
              </div>
            </div>

            {/* Right Column - Style Settings */}
            <div className="space-y-6">
              {/* Artist Name */}
              <div className="space-y-3">
                <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest flex items-center space-x-2">
                  <SafeIcon icon={FiMusic} className="w-3 h-3" />
                  <span>Artist Display</span>
                </label>
                <label className="flex items-center justify-between p-3 border border-gray-100 rounded-xl cursor-pointer hover:bg-gray-50 transition-colors">
                  <span className="text-sm font-bold text-gray-700">Show Artist Name</span>
                  <input 
                    type="checkbox" 
                    checked={settings.showArtistName} 
                    onChange={(e) => setSettings(s => ({ ...s, showArtistName: e.target.checked }))}
                    className="w-4 h-4 text-purple-500 rounded focus:ring-purple-500"
                  />
                </label>
                {settings.showArtistName && (
                  <input
                    type="text"
                    value={settings.artistName}
                    onChange={(e) => setSettings(s => ({ ...s, artistName: e.target.value }))}
                    className="w-full px-3 py-2.5 border border-gray-200 rounded-xl font-bold focus:outline-none focus:border-purple-400"
                    placeholder="Artist Name"
                  />
                )}
              </div>

              {/* Colors */}
              <div className="space-y-4">
                <ColorPickerMock 
                  label="Card Background" 
                  color={settings.cardBgColor} 
                  onChange={(c) => setSettings(s => ({ ...s, cardBgColor: c }))}
                />
                <ColorPickerMock 
                  label="Text Color" 
                  color={settings.textColor} 
                  onChange={(c) => setSettings(s => ({ ...s, textColor: c }))}
                />
                <ColorPickerMock 
                  label="Accent Color" 
                  color={settings.accentColor} 
                  onChange={(c) => setSettings(s => ({ ...s, accentColor: c }))}
                />
              </div>

              {/* Border Radius */}
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">
                    Card Border Radius
                  </label>
                  <span className="text-xs font-bold text-gray-500">{settings.borderRadius}px</span>
                </div>
                <input 
                  type="range" 
                  min="0" 
                  max="40" 
                  value={settings.borderRadius}
                  onChange={(e) => setSettings(s => ({ ...s, borderRadius: parseInt(e.target.value) }))}
                  className="w-full h-1.5 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-purple-500"
                />
              </div>

              {/* Preview Card */}
              <div className="border-t border-gray-100 pt-6">
                <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest block mb-3">
                  Card Preview
                </label>
                <div 
                  className="p-4 shadow-md border border-gray-100"
                  style={{ 
                    backgroundColor: settings.cardBgColor,
                    borderRadius: `${settings.borderRadius}px`
                  }}
                >
                  <h4 className="font-bold text-sm mb-1" style={{ color: settings.textColor }}>
                    Sample Track
                  </h4>
                  <p className="text-xs mb-2" style={{ color: settings.textColor, opacity: 0.7 }}>
                    Artist Name
                  </p>
                  <p className="text-xs mb-3" style={{ color: settings.textColor, opacity: 0.6 }}>
                    This is a sample description for your track.
                  </p>
                  <div className="flex space-x-2">
                    <div 
                      className="px-3 py-1.5 rounded-lg text-white text-xs font-bold"
                      style={{ backgroundColor: settings.accentColor }}
                    >
                      Spotify
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="p-5 bg-gray-50 border-t border-gray-100 flex space-x-3">
          <button 
            onClick={onClose}
            className="flex-1 py-3 font-bold text-gray-500 hover:bg-gray-200 rounded-xl transition-colors text-sm"
          >
            Cancel
          </button>
          <button
            onClick={handleApply}
            className="flex-[2] py-3 bg-purple-500 text-white font-bold rounded-xl hover:bg-purple-600 transition-colors text-sm flex items-center justify-center space-x-2"
          >
            <SafeIcon icon={FiCheck} className="w-4 h-4" />
            <span>Apply Settings</span>
          </button>
        </div>
      </motion.div>
    </div>
  );
};

export default ArtistWidgetPopup;